import { hasGoogleAdPrivacyChoices, showGoogleAdPrivacyChoices } from './Ads';

export type AnalyticsConsent = 'granted' | 'denied' | 'unset';

const STORAGE_KEY = 'spooky-stockings-privacy-v1';

interface StoredPrivacyChoice {
  version: 1;
  analytics: Exclude<AnalyticsConsent, 'unset'>;
  updatedAt: string;
}

let currentConsent: AnalyticsConsent = 'unset';
let consentChanged: ((analyticsAllowed: boolean) => void) | undefined;
let controlsBound = false;

export function parsePrivacyChoice(raw: string | null): AnalyticsConsent {
  if (!raw) return 'unset';
  try {
    const parsed = JSON.parse(raw) as Partial<StoredPrivacyChoice>;
    if (parsed.version !== 1) return 'unset';
    return parsed.analytics === 'granted' || parsed.analytics === 'denied' ? parsed.analytics : 'unset';
  } catch {
    return 'unset';
  }
}

function readConsent(): AnalyticsConsent {
  try {
    return parsePrivacyChoice(localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'unset';
  }
}

function persistConsent(consent: Exclude<AnalyticsConsent, 'unset'>): void {
  currentConsent = consent;
  try {
    const stored: StoredPrivacyChoice = {
      version: 1,
      analytics: consent,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // The choice still applies for this page view when storage is unavailable.
  }
  consentChanged?.(consent === 'granted');
}

function modal(): HTMLElement | null {
  return document.getElementById('privacy-dialog');
}

function analyticsCheckbox(): HTMLInputElement | null {
  return document.getElementById('privacy-analytics') as HTMLInputElement | null;
}

export function openPrivacyCenter(): void {
  const dialog = modal();
  const checkbox = analyticsCheckbox();
  if (!dialog || !checkbox) return;
  const wasHidden = dialog.hidden;
  checkbox.checked = currentConsent === 'granted';
  const adChoices = document.getElementById('google-ad-privacy');
  if (adChoices) adChoices.hidden = !hasGoogleAdPrivacyChoices();
  dialog.hidden = false;
  dialog.setAttribute('aria-hidden', 'false');
  checkbox.focus();
  if (wasHidden) document.dispatchEvent(new Event('privacy-center-opened'));
}

function closePrivacyCenter(): void {
  const dialog = modal();
  if (!dialog || dialog.hidden) return;
  dialog.hidden = true;
  dialog.setAttribute('aria-hidden', 'true');
  document.dispatchEvent(new Event('privacy-center-closed'));
  document.getElementById('open-privacy')?.focus();
}

/**
 * Binds the first-party privacy controls. This preference covers only optional
 * Application Insights telemetry; Google advertising consent remains owned by
 * Google's certified Privacy & Messaging CMP when production ads are enabled.
 */
export function initPrivacyControls(onConsentChanged: (analyticsAllowed: boolean) => void): AnalyticsConsent {
  consentChanged = onConsentChanged;
  currentConsent = readConsent();

  if (!controlsBound) {
    controlsBound = true;
    document.getElementById('open-privacy')?.addEventListener('click', openPrivacyCenter);
    document.getElementById('google-ad-privacy')?.addEventListener('click', () => {
      if (showGoogleAdPrivacyChoices()) closePrivacyCenter();
    });
    document.getElementById('privacy-necessary-only')?.addEventListener('click', () => {
      persistConsent('denied');
      closePrivacyCenter();
    });
    document.getElementById('privacy-save')?.addEventListener('click', () => {
      persistConsent(analyticsCheckbox()?.checked ? 'granted' : 'denied');
      closePrivacyCenter();
    });
  }

  if (currentConsent === 'unset') openPrivacyCenter();
  return currentConsent;
}
